import Joi from 'joi'

import { EntityConstants } from 'constants/EntityConstants'
import { DEFAULT_WAIT_AND_RETURN_FEE_CATEGORY_ID } from 'constants/index'
import { BookingStatus } from 'generated/graphql'
import { multipleEmailsSchema } from 'helpers/emailValidation.helpers'
import { phoneNumberSchema, phoneNumberOptionalSchema } from 'helpers/phoneValidation.helpers'
import { TBookingForm } from 'types'

import { validatePostcode } from './postcode.validation'
import {
  validateCollectionAt,
  validateCollectionBy,
  validateDeliveryAt,
  validateDeliveryBy,
} from './transit-time.validation'

const REQ_MSG = 'Wait and Return Fee is required when Wait & Return is selected'
const ZERO_MSG = 'Please select a valid Wait & Return Fee (0% is not allowed).'

const BookingDetailsValidation = Joi.object<TBookingForm>({
  // Booking Details
  customerId: Joi.string().empty(''),
  contactId: Joi.string().empty(''),
  customerReference: Joi.string()
    .empty('')
    .optional()
    .when('$mode', { is: 1, then: Joi.optional() })
    .when('$isQuote', { is: true, then: Joi.optional() })
    .when('$isQuickQuote', { is: true, then: Joi.optional() })
    .max(EntityConstants.MAX_CUSTOMER_REFERENCE_LENGTH),
  overrideContactEmail: Joi.string()
    .email({ multiple: true, separator: ';', tlds: false })
    .optional()
    .empty('')
    .max(EntityConstants.MAX_EMAIL_LENGTH),
  overrideContactTelephoneNumber: phoneNumberOptionalSchema.empty(''),
  noteOrSpecialInstruction: Joi.string().allow('').optional().max(EntityConstants.MAX_NOTE_LENGTH),
  alternativeOurReference: Joi.string()
    .empty('')
    .optional()
    .max(EntityConstants.MAX_GENERIC_TEXT_LENGTH),
  distance: Joi.number().when('$isQuickQuote', {
    is: false,
    then: Joi.number().greater(0).message('Distance must be greater than 0'),
  }),
  consignmentFee: Joi.number().empty(''),
  consignmentFeeFromDynamicPricing: Joi.number().empty('').allow(null).optional(),
  consignmentFeeFromTariff: Joi.number().empty('').allow(null).optional(),
  tariffId: Joi.string().empty('').required().messages({
    'any.required': 'Tariff is required',
    'string.empty': 'Tariff is required',
  }),

  waitAndReturnFeeCategoryId: Joi.alternatives().conditional('isWaitAndReturn', {
    is: true,
    then: Joi.string()
      .label('Wait and Return Fee Category')
      .trim()
      .min(1)
      .required()
      .invalid(DEFAULT_WAIT_AND_RETURN_FEE_CATEGORY_ID, null, '')
      .messages({
        'any.required': REQ_MSG,
        'string.empty': REQ_MSG,
        'string.min': REQ_MSG,
        'string.base': REQ_MSG,
        'any.invalid': ZERO_MSG,
      }),
    otherwise: Joi.string().allow(null, '').optional(),
  }),

  collectionAddresses: Joi.array().items(
    Joi.object({
      at: Joi.date().empty('').optional().custom(validateCollectionAt, 'Transit Time validation'),
      by: Joi.date()
        .empty('')
        .optional()
        .greater(Joi.ref('at'))
        .message(`Field must be greater then 'Ready At'`)
        .custom(validateCollectionBy, 'Transit Time validation'),
      name: Joi.string().empty('').when('$isQuickQuote', { is: true, then: Joi.optional() }),
      city: Joi.string().empty('').when('$isQuickQuote', { is: true, then: Joi.optional() }),
      addressLine1: Joi.string()
        .empty('')
        .when('$isQuickQuote', { is: true, then: Joi.optional() }),
      addressLine2: Joi.string().empty('').optional(),
      postcode: Joi.string()
        .empty('')
        .label('Postcode')
        .custom(validatePostcode, 'Postcode validation')
        .max(8),
      countryId: Joi.number(),
      contact: Joi.string()
        .empty('')
        .optional()
        .when('$isAllowedToMakeCollectionAndDeliveryContactDataRequired', {
          is: true,
          then: Joi.required(),
        }),
      email: multipleEmailsSchema,
      telephoneNumber: Joi.when('$isAllowedToMakeCollectionAndDeliveryContactDataRequired', {
        is: true,
        then: phoneNumberSchema,
        otherwise: phoneNumberOptionalSchema,
      }),
      note: Joi.string()
        .allow('')
        .optional()
        .max(EntityConstants.MAX_NOTE_LENGTH)
        .messages({
          'string.max': `Note for Driver must be ${EntityConstants.MAX_NOTE_LENGTH} characters or fewer`,
        }),
    }),
  ),

  deliveryAddresses: Joi.array().items(
    Joi.object({
      at: Joi.date().empty('').optional().custom(validateDeliveryAt, 'Transit Time validation'),
      by: Joi.date()
        .empty('')
        .optional()
        .greater(Joi.ref('at'))
        .message(`Field must be greater then 'Deliver From'`)
        .custom(validateDeliveryBy, 'Transit Time validation'),
      name: Joi.string().empty('').when('$isQuickQuote', { is: true, then: Joi.optional() }),
      city: Joi.string().empty('').when('$isQuickQuote', { is: true, then: Joi.optional() }),
      addressLine1: Joi.string()
        .empty('')
        .when('$isQuickQuote', { is: true, then: Joi.optional() }),
      addressLine2: Joi.string().empty('').optional(),
      postcode: Joi.string()
        .empty('')
        .label('Postcode')
        .custom(validatePostcode, 'Postcode validation')
        .max(8),
      countryId: Joi.number(),
      contact: Joi.string()
        .empty('')
        .optional()
        .when('$isAllowedToMakeCollectionAndDeliveryContactDataRequired', {
          is: true,
          then: Joi.required(),
        }),
      email: multipleEmailsSchema,
      telephoneNumber: Joi.when('$isAllowedToMakeCollectionAndDeliveryContactDataRequired', {
        is: true,
        then: phoneNumberSchema,
        otherwise: phoneNumberOptionalSchema,
      }),
      reasonCodeId: Joi.number().empty(0).optional().when('....isReasonCodeEnabled', {
        is: true,
        then: Joi.required(),
      }),
      note: Joi.string()
        .allow('')
        .optional()
        .max(EntityConstants.MAX_NOTE_LENGTH)
        .messages({
          'string.max': `Note for Driver must be ${EntityConstants.MAX_NOTE_LENGTH} characters or fewer`,
        }),
    }),
  ),

  packages: Joi.array().items(
    Joi.object({
      quantity: Joi.number()
        .label('Quantity')
        .empty('')
        .when('$isQuickQuote', { is: true, then: Joi.optional() })
        .when('....isPackageDetailsSectionOptional', { is: true, then: Joi.optional() }),
      packageType: Joi.string()
        .label('Package Type')
        .when('$isQuickQuote', { is: true, then: Joi.optional() })
        .when('....isPackageDetailsSectionOptional', { is: true, then: Joi.optional() }),
      collectionSequenceOrder: Joi.number()
        .label('Collection')
        .empty('')
        .when('$isQuickQuote', { is: true, then: Joi.optional() })
        .when('....isPackageDetailsSectionOptional', { is: true, then: Joi.optional() }),
      deliverySequenceOrder: Joi.number()
        .label('Delivery')
        .empty('')
        .when('$isQuickQuote', { is: true, then: Joi.optional() })
        .when('....isPackageDetailsSectionOptional', { is: true, then: Joi.optional() }),
      length: Joi.number()
        .label('Length')
        .empty('')
        .when('$isQuickQuote', { is: true, then: Joi.optional() })
        .when('....isPackageDetailsSectionOptional', { is: true, then: Joi.optional() }),
      width: Joi.number()
        .label('Width')
        .empty('')
        .when('$isQuickQuote', { is: true, then: Joi.optional() })
        .when('....isPackageDetailsSectionOptional', { is: true, then: Joi.optional() }),
      height: Joi.number()
        .label('Height')
        .empty('')
        .when('$isQuickQuote', { is: true, then: Joi.optional() })
        .when('....isPackageDetailsSectionOptional', { is: true, then: Joi.optional() }),
      weight: Joi.number()
        .label('Weight')
        .empty('')
        .when('$isQuickQuote', { is: true, then: Joi.optional() })
        .when('....isPackageDetailsSectionOptional', { is: true, then: Joi.optional() }),
      reference: Joi.string()
        .label('Reference')
        .allow('')
        .optional()
        .max(EntityConstants.MAX_PACKAGE_REFERENCE_LENGTH)
        .messages({
          'string.max': `Reference must be ${EntityConstants.MAX_PACKAGE_REFERENCE_LENGTH} characters or fewer`,
        }),
      details: Joi.string()
        .label('Details')
        .allow('')
        .optional()
        .max(EntityConstants.MAX_NAME_LENGTH)
        .messages({
          'string.max': `Details must be ${EntityConstants.MAX_NAME_LENGTH} characters or fewer`,
        }),
    })
      .label('Package')
      // Collection and Delivery are pre-filled from the stops, so on their own they do not make a
      // consignment "started": the goods fields are all-or-none, and a quantity requires its two
      // stops.
      .and('quantity', 'length', 'width', 'height', 'weight')
      .with('quantity', ['collectionSequenceOrder', 'deliverySequenceOrder']),
  ),

  // ToDo: add other fields
  charges: Joi.array()
    .optional()
    .items(
      Joi.object({
        quantity: Joi.number().optional(),
      }),
    ),
}).when(
  Joi.object({
    status: Joi.valid(
      BookingStatus.ProofOfDelivery,
      BookingStatus.Complete,
      BookingStatus.Invoice,
      BookingStatus.ScheduledTemplate,
    ),
    isReferenceRequired: Joi.valid(true),
  }).unknown(),
  {
    then: Joi.object({ customerReference: Joi.required() }),
    otherwise: Joi.object({ customerReference: Joi.optional() }),
  },
)

export { BookingDetailsValidation }
